// src/components/shared/ToastProvider.tsx
import { IconBaseProps } from 'react-icons';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { customUUIDv4 } from '@/utils/generic';
import ToastContainer, { ToastContainerProps } from './ToastContainer';
import { Truncate } from './ToastMessage';
import { renderErrorTemplate } from './ToastTemplate';

/// //////////////////////////////////
/// Types
/// //////////////////////////////////

export type ToastProviderProps = {
  children: React.ReactNode;
} & ToastContainerProps;

type TostMessageType = 'Info' | 'Success' | 'Warning' | 'Error';

export type Toast = {
  id: string;
  lifetime: number;
  message: string | React.ReactNode;
  type?: TostMessageType;
  truncate?: Truncate;
  icon?: IconBaseProps;
  header?: string;
};

export type ToastContextType = {
  data: Array<Toast>;
  // Updated to support React.ReactNode for all push methods
  pushError(
    message: string | React.ReactNode,
    lifetime?: number,
    truncate?: Truncate,
  ): void;
  pushWarning(
    message: string | React.ReactNode,
    lifetime?: number,
    truncate?: Truncate,
  ): void;
  pushSuccess(
    message: string | React.ReactNode,
    lifetime?: number,
    truncate?: Truncate,
  ): void;
  pushInfo(
    message: string | React.ReactNode,
    lifetime?: number,
    truncate?: Truncate,
  ): void;
  push(
    message: string | React.ReactNode,
    type: TostMessageType,
    lifetime?: number,
    truncate?: Truncate,
  ): void;
  pushCustom(
    message: string | React.ReactNode,
    lifetime?: number,
    truncate?: Truncate,
    icon?: IconBaseProps | React.ReactNode,
  ): void;
  remove(id: string): void;
  clearAll(): void;
};

/// //////////////////////////////////
/// Global and Helpers
/// //////////////////////////////////

export const ToastContext = React.createContext<ToastContextType | undefined>(
  undefined,
);

export const useToast = () => useContext(ToastContext);

const DEFAULT_INTERVAL = 4000;
const SUCCESS_LIFETIME = 3000;
const ERROR_LIFETIME = 6000;

/// //////////////////////////////////
/// Implementation
/// //////////////////////////////////

export default function ToastProvider({
  children,
  variant,
}: ToastProviderProps) {
  const [data, setData] = useState<Array<Toast>>([]);

  const Push = useCallback(
    (
      message: string | React.ReactNode,
      type: TostMessageType,
      lifetime?: number,
      truncate?: Truncate,
    ) => {
      if (message) {
        const new_item: Toast = {
          id: customUUIDv4(),
          message,
          type,
          lifetime: lifetime || DEFAULT_INTERVAL,
          truncate,
        };
        setData((prevState) => {
          // For Error toasts: remove all existing Error toasts so only 1 is ever shown
          if (type === 'Error') {
            return [...prevState.filter((t) => t.type !== 'Error'), new_item];
          }
          // Prevention of duplicate messages (only for strings)
          if (
            typeof message === 'string' &&
            prevState.some((t) => t.message === message)
          )
            return prevState;
          return [...prevState, new_item];
        });
      }
    },
    [],
  );

  const PushCustom = useCallback(
    (
      message: string | React.ReactNode,
      lifetime?: number,
      truncate?: Truncate,
      icon?: IconBaseProps,
    ) => {
      if (message) {
        const new_item: Toast = {
          id: customUUIDv4(),
          message,
          lifetime: lifetime || DEFAULT_INTERVAL,
          truncate,
          icon,
          type: undefined,
        };
        setData((prevState) => [...prevState, new_item]);
      }
    },
    [],
  );

  const PushError = useCallback(
    (
      message: string | React.ReactNode,
      lifetime?: number,
      truncate?: Truncate,
    ) => Push(message, 'Error', lifetime || ERROR_LIFETIME, truncate),
    [Push],
  );

  const PushWarning = useCallback(
    (
      message: string | React.ReactNode,
      lifetime?: number,
      truncate?: Truncate,
    ) => Push(message, 'Warning', lifetime, truncate),
    [Push],
  );

  const PushSuccess = useCallback(
    (
      message: string | React.ReactNode,
      lifetime?: number,
      truncate?: Truncate,
    ) => Push(message, 'Success', lifetime || SUCCESS_LIFETIME, truncate),
    [Push],
  );

  const PushInfo = useCallback(
    (
      message: string | React.ReactNode,
      lifetime?: number,
      truncate?: Truncate,
    ) => Push(message, 'Info', lifetime, truncate),
    [Push],
  );

  const Remove = useCallback((id: string) => {
    setData((prevState) => prevState.filter((e) => e.id !== id));
  }, []);

  const ClearAll = useCallback(() => {
    setData([]);
  }, []);

  // Clear all persistent Error toasts whenever the user navigates to a different route
  const location = useLocation();
  useEffect(() => {
    setData((prev) => prev.filter((t) => t.type !== 'Error'));
  }, [location.pathname, location.search]);

  // --- UPDATED: Global Event Listener ---
  useEffect(() => {
    const handleGlobalToast = (e: any) => {
      const { message, type, lifetime, truncate, payload } = e.detail;

      switch (type) {
        case 'Success':
          PushSuccess(message, lifetime, truncate);
          break;
        case 'Error':
          // Use template if payload is provided, otherwise fall back to string message
          const errorContent = payload ? renderErrorTemplate(payload) : message;
          PushError(errorContent, lifetime, truncate);
          break;
        case 'Warning':
          PushWarning(message, lifetime, truncate);
          break;
        case 'Info':
          PushInfo(message, lifetime, truncate);
          break;
        default:
          Push(message, type, lifetime, truncate);
      }
    };

    window.addEventListener('SHOW_GLOBAL_TOAST', handleGlobalToast);

    return () => {
      window.removeEventListener('SHOW_GLOBAL_TOAST', handleGlobalToast);
    };
  }, [PushSuccess, PushError, PushWarning, PushInfo, Push]);

  useEffect(() => {
    return () => {
      setData([]);
    };
  }, []);

  const ToastContextData = useCallback(() => {
    return {
      data,
      pushError: PushError,
      pushWarning: PushWarning,
      pushSuccess: PushSuccess,
      pushInfo: PushInfo,
      push: Push,
      pushCustom: PushCustom,
      remove: Remove,
      clearAll: ClearAll,
    };
  }, [
    data,
    PushError,
    PushWarning,
    PushSuccess,
    PushInfo,
    Push,
    PushCustom,
    Remove,
    ClearAll,
  ]);

  return (
    <ToastContext.Provider value={ToastContextData()}>
      <ToastContainer variant={variant} />
      {children}
    </ToastContext.Provider>
  );
}
